import { GenericFlyoutListMenuWidget, GenericMenuItemInfo } from '@tableau/widgets';
import React from 'react';

import { telemetryConsumerWrapper } from '../../../app/contexts/TelemetryConsumerWrapper';
import { ActionMenuItemClickedTelemetryContext } from '../../../app/contexts/TelemetryContexts';
import { ActionMenuItemClickedEventHandler } from '../../telemetryEvents/types/ContentTelemetryTypes';
import { ButtonControlledChildComponentProps } from './ButtonControlledComponent';

/**
 * These props are passed in by the component that instantiates the ButtonControlledComponent
 */
export interface GenericButtonControlledFlyoutListMenuOwnProps {
  readonly onComponentDidMount: () => void;
  readonly menuItems: GenericMenuItemInfo[];
  readonly onMenuItemSelected: (menuItemValue: string) => void;
  readonly menuStyle?: React.CSSProperties;
}

export type GenericButtonControlledFlyoutListMenuProps =
  ButtonControlledChildComponentProps &
  GenericButtonControlledFlyoutListMenuOwnProps;

/**
 * This is a wrapper around the FlyoutListMenuWidget that is meant to be used as the child component
 * of a ButtonControlledComponent, so we can have a menu whose visibility is controlled by a button.
 *
 * To use it, create a wrapper component that takes ButtonControlledChildComponentProps and merges them with
 * GenericButtonControlledFlyoutListMenuOwnProps, then passes all those props to GenericButtonControlledFlyoutListMenu.
 *
 * This wrapper takes care of positioning the menu adjacent to the button and closing the menu
 * in the appropriate situations, so that the instantiating component needs to worry only about passing
 * in the menu and the callback handler.
 */
export class GenericButtonControlledFlyoutListMenu
  extends React.PureComponent<GenericButtonControlledFlyoutListMenuProps> {

  public componentDidMount() {
    if (this.props.onComponentDidMount) {
      this.props.onComponentDidMount();
    }
  }

  public render(): JSX.Element {
    return telemetryConsumerWrapper(
      ActionMenuItemClickedTelemetryContext.Consumer,
      this.renderFlyoutListMenu
    );
  }

  private getAnchorRect = (): ClientRect => {
    const rect = this.props.anchorElement.getBoundingClientRect();
    return {
      bottom: rect.bottom - 1,
      top: rect.top + 1,
      width: rect.width,
      height: rect.height - 2,
      right: rect.right,
      left: rect.left
    };
  };

  private renderFlyoutListMenu = (sendActionMenuItemClickTelemetry: ActionMenuItemClickedEventHandler) => {
    const defaultMenuStyle: React.CSSProperties = { fontWeight: 'normal', width: '300px' };
    const menuStyle: React.CSSProperties = { ...defaultMenuStyle, ...this.props.menuStyle };

    return (
      <GenericFlyoutListMenuWidget
        allowAutoPosition={true}
        menuStyle={menuStyle}
        handleScrollOnScrollingRoot={this.props.closeMe}
        menuItemInfos={this.props.menuItems}
        onSelect={(menuItemValue: string) => {
          sendActionMenuItemClickTelemetry(menuItemValue);
          this.onMenuItemSelected(menuItemValue);
        }}
        requestCloseAll={this.props.closeMe}
        testId={this.props.testId}
        toggleEl={this.props.anchorElement}
        getAnchorClientRect={this.getAnchorRect}
        shouldReturnFocusToToggleEl={false} />
    );
  }

  private onMenuItemSelected = (menuItemValue: string): void => {
    this.props.closeMe();
    this.props.onMenuItemSelected(menuItemValue);
  }
}
